import { createRouter as createTanStackRouter } from '@tanstack/react-router';
import { rootRoute } from './__root';
import { indexRoute } from './index.route';
import { loginRoute } from './login.route';
import { authCallbackRoute } from './auth.callback.route';
import { authDiscordCallbackRoute } from './auth.discord.callback.route';
import { authenticatedRoute } from './_authenticated';
import { galleryRoute } from './gallery.route';
import { photoRoute } from './photo.route';
import { settingsRoute } from './settings';
import { albumsRoute } from './albums.route';
import { albumDetailRoute } from './albums.$albumId.route';
import { inviteRoute } from './invite.$token.route';
import { sharedAlbumRoute } from './share.album.$tokenOrSlug.route';
import { sharedPhotoRoute } from './share.photo.$token.route';
import { termsRoute } from './terms.route';
import { privacyRoute } from './privacy.route';
import { cookiePolicyRoute } from './cookie-policy.route';
import { copyrightRoute } from './copyright.route';
import { licensesRoute } from './licenses.route';
import { aboutRoute } from './about.route';
import { downloadRoute } from './download.route';
import { pricingRoute } from './pricing.route';
import { adminRoute } from './_admin';
import { adminIndexRoute } from './admin.index';
import { adminUsersRoute } from './admin.users';
import { adminUserDetailRoute } from './admin.users.$userId';
import { adminPhotosRoute } from './admin.photos';
import { adminAlbumsRoute } from './admin.albums';
import { adminAlbumDetailRoute } from './admin.albums.$albumId';
import { adminMaintenanceRoute } from './admin.maintenance';

// Singleton router instance
let routerInstance: ReturnType<typeof createTanStackRouter> | null = null;

// Create router instance (singleton pattern to prevent duplicate routes)
export function createRouter() {
  if (routerInstance) {
    return routerInstance;
  }

  // TanStack Router v1 requires BOTH getParentRoute() AND addChildren():
  // - getParentRoute() provides TypeScript type inference for route params/context
  // - addChildren() builds the actual route tree structure
  // See: https://github.com/TanStack/router/discussions/585
  const routeTree = rootRoute.addChildren([
    indexRoute,
    loginRoute,
    authCallbackRoute,
    authDiscordCallbackRoute,
    inviteRoute,
    sharedAlbumRoute,
    sharedPhotoRoute,
    termsRoute,
    privacyRoute,
    cookiePolicyRoute,
    copyrightRoute,
    licensesRoute,
    aboutRoute,
    downloadRoute,
    pricingRoute,
    authenticatedRoute.addChildren([
      galleryRoute,
      photoRoute,
      settingsRoute,
      albumsRoute,
      albumDetailRoute,
    ]),
    adminRoute.addChildren([
      adminIndexRoute,
      adminUsersRoute,
      adminUserDetailRoute,
      adminPhotosRoute,
      adminAlbumsRoute,
      adminAlbumDetailRoute,
      adminMaintenanceRoute,
    ]),
  ]);

  routerInstance = createTanStackRouter({
    routeTree,
    defaultPreload: 'intent',
    defaultNotFoundComponent: () => (
      <div data-testid="not-found">
        <h1>404 - Page Not Found</h1>
        <p>The page you are looking for does not exist.</p>
      </div>
    ),
  });

  return routerInstance;
}

// Create default router instance for app
export const router = createRouter();

// Type augmentation for router
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
